<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useMachine } from "@xstate/vue"
import { useResponsive, type OwnerOutput } from "@core/composables"
import { createSourceFormMultiMachine } from "@core/machines"
import type { Building } from "@core/components/source/multi/SourceMultiAppendFormBuilding.vue"
import type {
    SourceFormMultiInvitation,
    SourceFormMultiContext,
} from "@core/machines"
import type { MultiConsentBuildingRecap } from "@core/types/source/multi_consent"
import type { ConsentRequest } from "@types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            timeline: {
                step1: "Coordonnées titulaire",
                step2: "Ajout des bâtiments et compteurs",
                step3: "Finalisation",
            },
            ownerStep: {
                helperTitle: "Qui est le titulaire de ces sources ?",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Emits {
    /**
     * Émis à la fin du parcours (étape Fin ou Terminer depuis Envoi invitation)
     * @param type - 'invitation' si l'utilisateur a quitté depuis l'étape Envoi invitation, sinon undefined
     */
    (e: "done:form", type?: string): void
}

// ================================
// EMITS
// ================================
const emit = defineEmits<Emits>()

// ================================
// RESPONSIVE
// ================================
const { isMobile } = useResponsive()

// ================================
// MACHINE
// ================================
const machine = createSourceFormMultiMachine()
const { snapshot, send } = useMachine(machine)

const isStorybook = import.meta.env.STORYBOOK === "true"
const machineStateForStorybook = isStorybook
    ? reactive<{ value: unknown; context: unknown }>({
          value: undefined,
          context: undefined,
      })
    : undefined

if (isStorybook && machineStateForStorybook) {
    watch(
        snapshot,
        (s) => {
            machineStateForStorybook.value = s.value
            machineStateForStorybook.context = s.context
        },
        { immediate: true }
    )
}

defineExpose(
    isStorybook && machineStateForStorybook
        ? { machineState: machineStateForStorybook }
        : {}
)

/** Contexte initial (fallback avant que le snapshot soit disponible) */
const INITIAL_CONTEXT: SourceFormMultiContext = {
    owner: {},
    thirdParty: false,
    buildings: [],
    invitation: null,
    currentStep: 1,
    done: false,
}

// ================================
// STATE
// ================================
/** Durée du consentement en mois (constante ; pourra venir du scope/config plus tard) */
const CONSENT_DURATION_MONTHS = 60

/** Brouillon titulaire (synchronisé au retour sur l’étape ; soumis au clic Suivant uniquement) */
const ownerDraft = ref<OwnerOutput | null>(null)

// ================================
// CONSTANTS
// ================================
/**
 * États de la machine (ordre d’itération pour `matches` — un seul actif à la fois).
 * Ne pas utiliser `snapshot.value` brut : en XState v5 `StateValue` peut être un objet ;
 * `matches(id)` est fiable pour brancher le template.
 */
const MULTI_FORM_STATE_NAMES = [
    "SourceFormOwner",
    "SourceMultiAppendFormBuilding",
    "SourceMultiAppendFormSummary",
    "SourceMultiAppendFormInvitationSent",
    "SourceMultiAppendFormConsentSign",
    "SourceMultiAppendFormFinish",
] as const

// ================================
// COMPUTED
// ================================
/** Nom de l'état courant de la machine */
const getCurrentStateName = computed(
    (): (typeof MULTI_FORM_STATE_NAMES)[number] => {
        const s = snapshot.value
        if (!s) return "SourceFormOwner"
        for (const name of MULTI_FORM_STATE_NAMES) {
            if (s.matches(name)) return name
        }
        return "SourceFormOwner"
    }
)

/** Contexte de la machine */
const getContext = computed(
    (): SourceFormMultiContext => snapshot.value?.context ?? INITIAL_CONTEXT
)

watch(getCurrentStateName, (name, prev) => {
    if (name !== "SourceFormOwner") return
    if (
        prev === "SourceMultiAppendFormBuilding" ||
        prev === "SourceMultiAppendFormSummary"
    ) {
        const o = getContext.value.owner
        if (o && Object.keys(o).length > 0) {
            ownerDraft.value = o as OwnerOutput
        }
    }
})

/**
 * Indice d’étape timeline (0–2), ou 3 si parcours terminé (écran Fin).
 * 1: SourceFormOwner — 2: Building+Summary — 3: SendInvitation|ConsentSign|Finish
 */
const getActiveTimelineStepIndex = computed((): number => {
    const state = getCurrentStateName.value
    const ctx = getContext.value
    if (ctx.done && state === "SourceMultiAppendFormFinish") return 3
    if (state === "SourceFormOwner") return 0
    if (
        state === "SourceMultiAppendFormBuilding" ||
        state === "SourceMultiAppendFormSummary"
    ) {
        return 1
    }
    if (
        state === "SourceMultiAppendFormInvitationSent" ||
        state === "SourceMultiAppendFormConsentSign" ||
        state === "SourceMultiAppendFormFinish"
    ) {
        return 2
    }
    return 0
})

/** Items pour PrimeVue Timeline (BaseTimeline) */
const getTimelineItems = computed(() => {
    const labels = [
        t("timeline.step1"),
        t("timeline.step2"),
        t("timeline.step3"),
    ]
    const active = getActiveTimelineStepIndex.value
    return labels.map((label, index) => {
        let status: "completed" | "current" | "disabled"
        if (active === 3) {
            status = "completed"
        } else if (index < active) {
            status = "completed"
        } else if (index === active) {
            status = "current"
        } else {
            status = "disabled"
        }
        return { label, status, stepNumber: index + 1 }
    })
})

/**
 * Étape courante pour l’en-tête mobile (numéro + titre uniquement, sans timeline).
 */
const getCurrentMobileStep = computed(() => {
    const active = getActiveTimelineStepIndex.value
    const items = getTimelineItems.value
    const idx = active >= 3 ? 2 : active
    const item = items[idx]
    return {
        stepNumber: item.stepNumber,
        label: item.label,
        isDone: active === 3,
    }
})

/** Styles des marqueurs (alignés sur SubscriptionFormHeader / parcours abonnement) */
const timelineStatusMarkerClass: Record<string, string> = {
    completed: "border-green-500 bg-green-500 text-white",
    current: "border-primary bg-primary text-white",
    disabled: "border-surface-300 bg-surface-100 text-surface-500",
}

const timelineStatusIcon: Record<string, string> = {
    completed: "pi pi-check",
}

/** Liste bâtiments pour l’étape saisie (alignée sur le type attendu par le composant enfant) */
const getBuildingsForStep = computed(
    (): Building[] => (getContext.value.buildings ?? []) as Building[]
)

/** Bâtiments filtrés pour la signature (sources en erreur exclues si l’API les signale) */
const getConsentBuildingsFiltered = computed(
    (): MultiConsentBuildingRecap[] => {
        const ctx = getContext.value
        const invalids = ctx.invitation?.invalidConsentRequests ?? []
        const invalidSourceKeys = new Set(
            invalids.map(
                (i) => `${String(i.idtype).toLowerCase()}-${i.idsource}`
            )
        )
        return (ctx.buildings ?? []).map((b) => {
            if (invalidSourceKeys.size === 0) {
                return b as MultiConsentBuildingRecap
            }
            return {
                ...b,
                sources: b.sources.filter(
                    (s) => !invalidSourceKeys.has(`${s.idtype}-${s.idsource}`)
                ),
            } as MultiConsentBuildingRecap
        })
    }
)

/** Invalides multi pour l’écran de signature (typage API) */
const getInvalidConsentRequestsForSign = computed((): ConsentRequest[] => {
    return (getContext.value.invitation?.invalidConsentRequests ??
        []) as unknown as ConsentRequest[]
})

// ================================
// INTERACTIONS
// ================================

/**
 * Passe à l’étape bâtiments après validation du titulaire (bouton Suivant)
 */
function onOwnerSubmit(): void {
    const owner = ownerDraft.value
    if (!owner) return
    send({
        type: "SUBMIT",
        value: owner,
        owner,
    })
}

/**
 * Passe aux compteurs après saisie des bâtiments
 */
function onSubmitBuildings(
    buildings: SourceFormMultiContext["buildings"]
): void {
    send({ type: "SUBMIT", value: buildings })
}

function onSubmitSummary(invitation: unknown | null): void {
    send({
        type: "SUBMIT",
        value: invitation as SourceFormMultiInvitation | null,
    })
}

function onClickSummaryUpdate(step: "owner" | "buildings"): void {
    if (step === "buildings") send({ type: "BACK" })
    else send({ type: "CHANGE_OWNER" })
}

function onSendInvitationSent(): void {
    send({ type: "SENT" })
}

function onConsentSignDone(): void {
    send({ type: "SUBMIT" })
}

function onFinishQuit(): void {
    emit("done:form")
}

function onInvitationQuit(): void {
    emit("done:form", "invitation")
}
</script>

<template>
    <div class="form-source-multi flex flex-col gap-6 w-full">
        <template v-if="getTimelineItems.length">
            <div
                v-if="isMobile"
                class="form-source-multi__step-mobile flex items-center gap-3 w-full"
            >
                <span
                    :class="[
                        'min-w-10 min-h-10 max-w-10 max-h-10 flex items-center justify-center rounded-full border text-sm font-medium shrink-0',
                        getCurrentMobileStep.isDone
                            ? timelineStatusMarkerClass.completed
                            : timelineStatusMarkerClass.current,
                    ]"
                >
                    <i
                        v-if="getCurrentMobileStep.isDone"
                        class="pi pi-check text-white block"
                        style="font-size: 1rem"
                    />
                    <span v-else class="leading-none">
                        {{ getCurrentMobileStep.stepNumber }}
                    </span>
                </span>
                <span
                    class="text-sm font-medium text-left min-w-0"
                    :class="
                        getCurrentMobileStep.isDone
                            ? 'text-green-700'
                            : 'text-primary'
                    "
                >
                    {{ getCurrentMobileStep.label }}
                </span>
            </div>

            <BaseTimeline
                v-else
                :value="getTimelineItems"
                class="form-source-multi__timeline w-full"
                layout="horizontal"
                align="top"
            >
                <template #marker="slotProps">
                    <span
                        :class="[
                            'min-w-10 min-h-10 max-w-10 max-h-10 flex items-center justify-center rounded-full border text-sm font-medium',
                            timelineStatusMarkerClass[slotProps.item.status] ??
                                timelineStatusMarkerClass.disabled,
                        ]"
                    >
                        <i
                            v-if="timelineStatusIcon[slotProps.item.status]"
                            style="font-size: 1rem"
                            :class="[
                                'text-white block',
                                timelineStatusIcon[slotProps.item.status],
                            ]"
                        />
                        <span v-else class="leading-none">
                            {{ slotProps.item.stepNumber }}
                        </span>
                    </span>
                </template>
                <template #opposite="slotProps">
                    <span
                        class="text-sm font-medium text-center inline-block"
                        :class="[
                            slotProps.item.status === 'current' &&
                                'text-primary',
                            slotProps.item.status === 'completed' &&
                                'text-green-700',
                            slotProps.item.status === 'disabled' &&
                                'text-surface-400',
                        ]"
                    >
                        {{ slotProps.item.label }}
                    </span>
                </template>
            </BaseTimeline>
        </template>

        <SourceFormOwner
            v-if="getCurrentStateName === 'SourceFormOwner'"
            v-model="ownerDraft"
            class="modal-source-factory-step w-full"
            :helper-title="t('ownerStep.helperTitle')"
        >
            <template #footer>
                <div
                    class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-end"
                >
                    <BaseButton
                        label="Suivant"
                        class="min-w-[10rem]"
                        :disabled="!ownerDraft"
                        @click="onOwnerSubmit"
                    />
                </div>
            </template>
        </SourceFormOwner>

        <SourceMultiAppendFormBuilding
            v-else-if="getCurrentStateName === 'SourceMultiAppendFormBuilding'"
            class="modal-source-factory-step w-full"
            :model-value="getBuildingsForStep"
            @click:submit="onSubmitBuildings"
            @click:return="send({ type: 'BACK' })"
        />

        <SourceMultiAppendFormSummary
            v-else-if="getCurrentStateName === 'SourceMultiAppendFormSummary'"
            class="modal-source-factory-step w-full"
            :owner="getContext.owner ?? {}"
            :third-party="getContext.thirdParty"
            :buildings="getContext.buildings ?? []"
            @done:submit="onSubmitSummary"
            @click:return="send({ type: 'BACK' })"
            @click:update="onClickSummaryUpdate"
        />

        <SourceMultiAppendFormInvitationSent
            v-else-if="
                getCurrentStateName === 'SourceMultiAppendFormInvitationSent'
            "
            class="modal-source-factory-step w-full"
            :request-id="getContext.invitation?._id ?? ''"
            :link="getContext.invitation?.url ?? ''"
            @sent="onSendInvitationSent"
            @click:quit="onInvitationQuit"
        />

        <SourceMultiAppendFormConsentSign
            v-else-if="
                getCurrentStateName === 'SourceMultiAppendFormConsentSign'
            "
            class="modal-source-factory-step w-full"
            :request-id="getContext.invitation?._id ?? ''"
            :owner="getContext.owner ?? {}"
            :buildings-creation="getContext.buildings ?? []"
            :buildings="getConsentBuildingsFiltered"
            :duration="CONSENT_DURATION_MONTHS"
            :invalid-consent-requests="getInvalidConsentRequestsForSign"
            @done:sign="onConsentSignDone"
            @click:return="send({ type: 'BACK' })"
        />

        <SourceMultiAppendFormFinish
            v-else-if="getCurrentStateName === 'SourceMultiAppendFormFinish'"
            class="modal-source-factory-step w-full mx-auto"
            @click:quit="onFinishQuit"
        />
    </div>
</template>
